<script setup lang="ts">
import { select, arc, interpolate, interpolateRgb } from 'd3';
import { FunctionalComponent, onMounted, ref, watchEffect } from 'vue';
import { calculateDeltaValue, calculateMetricValue, DeltaType, MetricType, formatDeltaValue } from './../utility';
import { roundAwayFromZero } from '@Shared/utils/util';

import DownArrowSvg from '../visualAssets/DownArrow.svg?component';
import UpArrowSvg from '../visualAssets/UpArrow.svg?component';
import LevelArrowSvg from '../visualAssets/LevelArrow.svg?component';
import PurpleDownArrowSvg from '../visualAssets/PurpleDownArrow.svg?component';
import PurpleUpArrowSvg from '../visualAssets/PurpleUpArrow.svg?component';
import PurpleLevelArrowSvg from '../visualAssets/PurpleLevelArrow.svg?component';
import { MeasureResult } from '@Shared/utils/types';

const svgRef = ref<SVGElement | null>(null);

const props = defineProps<{
  metricType: MetricType;
  color: string;
  driver: MeasureResult;
  title: string;
  inPopup: boolean;
  usePurpleGauge: boolean;
}>();

const deltaValue = computed(() => calculateDeltaValue(props.driver, DeltaType.PointDelta));

const deltaColor = computed<string | null>(() => {
  if (deltaValue.value == null) return null;

  if (props.usePurpleGauge) return '#621A4B';

  if (roundAwayFromZero(deltaValue.value * 100) < 0) {
    return '#D4002B';
  } else if (roundAwayFromZero(deltaValue.value * 100) > 0) {
    return '#0C8A65';
  } else {
    return '#7F7466';
  }
});

const deltaImg = computed<FunctionalComponent | null>(() => {
  if (deltaValue.value == null) return null;

  if (roundAwayFromZero(deltaValue.value * 100) < 0) {
    if (props.usePurpleGauge) return PurpleDownArrowSvg;
    return DownArrowSvg;
  } else if (roundAwayFromZero(deltaValue.value * 100) > 0) {
    if (props.usePurpleGauge) return PurpleUpArrowSvg;
    return UpArrowSvg;
  } else {
    if (props.usePurpleGauge) return PurpleLevelArrowSvg;
    return LevelArrowSvg;
  }
});

const value = computed<string>(() => {
  return calculateMetricValue(props.driver.currentValue, props.metricType);
});
const delta = computed<string>(() => {
  return formatDeltaValue(deltaValue.value, DeltaType.PointDelta);
});

let oldAngle = 0;
let oldColor = '';

onMounted(() => {
  const svg = select(svgRef.value);

  watchEffect(() => {
    if (value.value == null || props.driver.currentValue == null || props.color == null) return;

    svg.selectAll('*').remove();

    const gauge = arc().cornerRadius(30).startAngle(0);

    svg
      .append('path')
      .datum({
        endAngle: Math.PI * 2,
        innerRadius: 80,
        outerRadius: 90,
      })
      .attr('fill', '#D9D9D9')
      .attr('d', gauge as any);

    const foreground = svg
      .append('path')
      .datum({
        endAngle: oldAngle,
        innerRadius: 75,
        outerRadius: 90,
      })
      .attr('fill', oldColor)
      .attr('d', gauge as any);

    svg
      .append('text')
      .text(value.value)
      .attr('text-anchor', 'middle')
      .attr('transform', 'translate(0, -10)')
      .attr('font-size', '30')
      .attr('font-weight', '650')
      .attr('fill', '#333333');

    svg
      .append('text')
      .text(props.title)
      .attr('font-size', '16')
      .attr('font-weight', '400')
      .attr('text-anchor', 'middle')
      .attr('fill', '#333333')
      .attr('transform', 'translate(0, 120)');

    if (delta.value) {
      svg
        .append('text')
        .text(delta.value)
        .attr('text-anchor', 'middle')
        .attr('transform', 'translate(8, 20)')
        .attr('font-size', '20')
        .attr('font-weight', '700')
        .attr('fill', deltaColor.value);
    }

    let baseValue = props.driver.currentValue;

    // We now support NPS and Csat
    // For NPS we:
    // *100 to get from -1 -> 1 to -100 -> 100
    // Add 100 to get from 0 -> 200 to make percentages easier
    // Division by 200 to get percentage of angle
    // For Csat value is already valid percentage from 0 -> 1
    if (props.metricType == MetricType.Nps) baseValue = (baseValue * 100 + 100) / 200;
    const angle = (Math.PI / 180) * Math.floor(baseValue * 360);

    oldAngle = angle;
    const prevColor = oldColor;
    oldColor = props.color;

    if (import.meta.env.VITE_DISABLE_ANIMATION != 'true') {
      const anim = foreground.transition().delay(500).duration(1000);
      anim.styleTween('fill', () => interpolateRgb(prevColor, props.color));
      anim.call((transition, newAngle) => {
        transition.attrTween('d', (d) => {
          const fill = interpolate(d.endAngle, newAngle);
          return (t) => {
            d.endAngle = fill(t);
            return gauge(d as any) as any;
          };
        });
      }, angle);
    } else {
      // if we are disabling animations just skip directly to the value
      foreground.style('fill', props.color);
      foreground.attr('d', (d) => {
        d.endAngle = angle;
        return gauge(d as any) as any;
      });
    }
  });
});
</script>
<template>
  <svg :class="'w-[33%] h-full'" viewBox="-125 -95 260 300">
    <g ref="svgRef"></g>
    <foreignObject x="-110" y="130" width="230" height="80">
      <p class="font-bold text-2xl text-center break-words text-[#333333] truncate m-0">
        {{ props.driver.label }}
      </p>
    </foreignObject>
    <foreignObject x="-37" y="3" width="100" height="100">
      <component width="20" height="20" v-if="deltaImg" :is="deltaImg"></component>
    </foreignObject>
  </svg>
</template>
